/**
 * Email Sync History Service
 * 
 * Manages sync metadata, history, and status for email synchronization.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import type { InboxProviderId } from './inboxSync';

const SYNC_HISTORY_KEY = '@tripflow_email_sync_history';
const SYNC_METADATA_KEY = '@tripflow_email_sync_metadata';
const MAX_HISTORY_ENTRIES = 50;

export type SyncHistoryEntry = {
  id: string;
  tripId: string;
  provider: InboxProviderId;
  timestamp: number;
  bookingsFound: number;
  success: boolean;
  error?: string;
  duration?: number; // Duration in milliseconds
};

export type SyncMetadata = {
  [tripId: string]: {
    [provider in InboxProviderId]?: {
      lastSyncAt: number;
      lastSyncSuccess: boolean;
      totalBookingsFound: number;
      syncCount: number;
    };
  };
};

/**
 * Save a sync history entry
 */
export async function saveSyncHistory(entry: SyncHistoryEntry): Promise<void> {
  try {
    const stored = await AsyncStorage.getItem(SYNC_HISTORY_KEY);
    const history: SyncHistoryEntry[] = stored ? JSON.parse(stored) : [];
    
    // Add new entry at the beginning
    history.unshift(entry);
    
    // Keep only the most recent entries
    const trimmedHistory = history.slice(0, MAX_HISTORY_ENTRIES);
    
    await AsyncStorage.setItem(SYNC_HISTORY_KEY, JSON.stringify(trimmedHistory));
    
    // Update metadata
    await updateSyncMetadata(entry);
  } catch (error) {
    console.error('[EmailSyncHistory] Error saving sync history:', error);
  }
}

/**
 * Get sync history for a trip
 */
export async function getSyncHistory(tripId?: string, provider?: InboxProviderId): Promise<SyncHistoryEntry[]> {
  try {
    const stored = await AsyncStorage.getItem(SYNC_HISTORY_KEY);
    if (!stored) return [];
    
    const history: SyncHistoryEntry[] = JSON.parse(stored);
    
    // Filter by tripId and/or provider if provided
    let filtered = history;
    if (tripId) {
      filtered = filtered.filter(entry => entry.tripId === tripId);
    }
    if (provider) {
      filtered = filtered.filter(entry => entry.provider === provider);
    }
    
    return filtered;
  } catch (error) {
    console.error('[EmailSyncHistory] Error getting sync history:', error);
    return [];
  }
}

/**
 * Get all sync history
 */
export async function getAllSyncHistory(): Promise<SyncHistoryEntry[]> {
  return getSyncHistory();
}

/**
 * Update sync metadata
 */
async function updateSyncMetadata(entry: SyncHistoryEntry): Promise<void> {
  try {
    const stored = await AsyncStorage.getItem(SYNC_METADATA_KEY);
    const metadata: SyncMetadata = stored ? JSON.parse(stored) : {};
    
    if (!metadata[entry.tripId]) {
      metadata[entry.tripId] = {};
    }
    
    const tripMetadata = metadata[entry.tripId];
    if (!tripMetadata[entry.provider]) {
      tripMetadata[entry.provider] = {
        lastSyncAt: 0,
        lastSyncSuccess: false,
        totalBookingsFound: 0,
        syncCount: 0,
      };
    }
    
    const providerMetadata = tripMetadata[entry.provider]!;
    providerMetadata.lastSyncAt = entry.timestamp;
    providerMetadata.lastSyncSuccess = entry.success;
    providerMetadata.syncCount += 1;
    
    if (entry.success) {
      providerMetadata.totalBookingsFound += entry.bookingsFound;
    }
    
    await AsyncStorage.setItem(SYNC_METADATA_KEY, JSON.stringify(metadata));
  } catch (error) {
    console.error('[EmailSyncHistory] Error updating sync metadata:', error);
  }
}

/**
 * Get sync metadata for a trip and provider
 */
export async function getSyncMetadata(
  tripId: string,
  provider: InboxProviderId
): Promise<{
  lastSyncAt: number | null;
  lastSyncSuccess: boolean;
  totalBookingsFound: number;
  syncCount: number;
}> {
  try {
    const stored = await AsyncStorage.getItem(SYNC_METADATA_KEY);
    if (!stored) {
      return {
        lastSyncAt: null,
        lastSyncSuccess: false,
        totalBookingsFound: 0,
        syncCount: 0,
      };
    }
    
    const metadata: SyncMetadata = JSON.parse(stored);
    const providerMetadata = metadata[tripId]?.[provider];
    
    if (!providerMetadata) {
      return {
        lastSyncAt: null,
        lastSyncSuccess: false,
        totalBookingsFound: 0,
        syncCount: 0,
      };
    }
    
    return {
      lastSyncAt: providerMetadata.lastSyncAt || null,
      lastSyncSuccess: providerMetadata.lastSyncSuccess,
      totalBookingsFound: providerMetadata.totalBookingsFound,
      syncCount: providerMetadata.syncCount,
    };
  } catch (error) {
    console.error('[EmailSyncHistory] Error getting sync metadata:', error);
    return {
      lastSyncAt: null,
      lastSyncSuccess: false,
      totalBookingsFound: 0,
      syncCount: 0,
    };
  }
}

/**
 * Get sync metadata for all trips
 */
export async function getAllSyncMetadata(): Promise<SyncMetadata> {
  try {
    const stored = await AsyncStorage.getItem(SYNC_METADATA_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('[EmailSyncHistory] Error getting all sync metadata:', error);
    return {};
  }
}

/**
 * Clear sync history for a trip
 */
export async function clearSyncHistory(tripId?: string): Promise<void> {
  try {
    if (tripId) {
      // Clear history for specific trip
      const history = await getAllSyncHistory();
      const filtered = history.filter(entry => entry.tripId !== tripId);
      await AsyncStorage.setItem(SYNC_HISTORY_KEY, JSON.stringify(filtered));
      
      // Clear metadata for specific trip
      const metadata = await getAllSyncMetadata();
      delete metadata[tripId];
      await AsyncStorage.setItem(SYNC_METADATA_KEY, JSON.stringify(metadata));
    } else {
      // Clear all history
      await AsyncStorage.removeItem(SYNC_HISTORY_KEY);
      await AsyncStorage.removeItem(SYNC_METADATA_KEY);
    }
  } catch (error) {
    console.error('[EmailSyncHistory] Error clearing sync history:', error);
  }
}

